import { setCookie, getCookie } from "./core/cookies";
import { uuidv4, getUTMParameters } from "./core/utils";
import { getDeviceInformation } from "./core/device";
import { getLocationData } from "./core/location";
import { setNewSessionId } from "./core/sessions";
import { setLocationVisitor } from "./core/location";

export type DmpConfig = {
  endpoint: string;
  domain?: string;
  authToken?: string;
  enabled?: boolean;
};

export type TrackOptions = {
  place?: string;
  pathname?: string | null;
  utm_data?: Record<string, string | null> | null;
};

export class DMPClient {
  private cfg:
    | (Required<Pick<DmpConfig, "endpoint" | "domain" | "enabled">> & {
        authToken: string | null;
      })
    | null = null;

  private inited = false;
  private queue: Array<[string, TrackOptions | undefined]> = [];

  init(config: DmpConfig): void {
    if (this.inited) return;
    this.cfg = {
      endpoint: config.endpoint,
      domain: config.domain || location.hostname,
      enabled: config.enabled !== false,
      authToken: config.authToken || null,
    };

    this.ensureIds(this.cfg.domain);

    setLocationVisitor({ cookieDomain: this.cfg.domain });

    this.inited = true;

    while (this.queue.length) {
      const [evt, opts] = this.queue.shift()!;
      this.track(evt, opts);
    }
  }

  identify(userId: string): void {
    if (!this.cfg) return;
    if (userId && !getCookie("dmp_user_id")) {
      setCookie("dmp_user_id", userId, {
        domain: this.cfg.domain,
        secure: true,
      });
    }
  }

  track(eventType: string, opts: TrackOptions = {}): Promise<void> | void {
    if (!eventType) return;
    if (!this.inited || !this.cfg) {
      this.queue.push([eventType, opts]);
      return;
    }
    const cfg = this.cfg;

    const event_id = uuidv4();
    const sessionId = getCookie("dmp_session_id");
    const eventDuration =
      eventType === "button" ? 0 : Number(getCookie("dmp_event_duration"));
    const location = getLocationData();
    const userdmp = getCookie("dmp_user_id");
    const deviceId =
      getCookie("dmp_device_id") || localStorage.getItem("dmp_device_id");

    const userCountry = location?.country || "Unknown";

    const payload = {
      user_information: {
        visitor_id: deviceId,
        user_id: userdmp ?? null,
        location: {
          country: userCountry,
          state: location?.state || null,
          city: location?.city || null,
          zip: location?.zip || null,
          latitude: location?.latitude || null,
          longitude: location?.longitude || null,
        },
      },
      device_information: getDeviceInformation(deviceId),
      event_information: {
        event_type: eventType,
        event_id,
        session_id: sessionId,
        event_timestamp: new Date().toISOString(),
        event_duration: Number.isFinite(eventDuration) ? eventDuration : null,
        reference_page:
          opts.place || (document.referrer ? "referral" : "direct"),
        event_url: opts.pathname ?? window.location.href,
        utm_parameters: opts.utm_data || getUTMParameters(),
      },
    };
    setCookie("dmp_last_activity_seen", new Date().toISOString(), {
      domain: cfg.domain,
      secure: true,
    });

    return this.send(payload);
  }
  private ensureIds(domain: string): void {
    if (!getCookie("dmp_session_id")) {
      setNewSessionId(domain);
    }

    if (!getCookie("dmp_device_id") && !localStorage.getItem("dmp_device_id")) {
      const id = uuidv4();
      setCookie("dmp_device_id", id, { domain, secure: true, expires: 365 });
      localStorage.setItem("dmp_device_id", id);
    }
  }
  private async send(bodyObj: unknown): Promise<void> {
    if (!this.cfg || !this.cfg.enabled) return;
    const body = JSON.stringify(bodyObj);

    try {
      await fetch(this.cfg.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(this.cfg.authToken ? { Authorization: this.cfg.authToken } : {}),
        },
        keepalive: true,
        body,
      });
    } catch (e) {
      console.error("[DMP] send error", e);
    }
  }
}
